import React, { useEffect, useState } from 'react';
import { alpacaService, ErrorLogEntry } from '../../services/AlpacaService';
import SignalLoader from '../../components/SignalLoader/SignalLoader';
import styles from './ErrorLog.module.css';

type SourceFilter = 'all' | 'validation' | 'bot' | 'strategist';

function formatTimestamp(ts: string): string {
  if (!ts) return '—';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return ts;
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

const ErrorLog: React.FC = () => {
  const [entries, setEntries] = useState<ErrorLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState<SourceFilter>('all');

  useEffect(() => {
    const fetchLog = async () => {
      setLoading(true);
      try {
        const data = await alpacaService.getErrorLog({
          source,
          limit: '100',
        });
        setEntries(data.entries || []);
      } catch (err) {
        console.error('Error fetching error log:', err);
        setEntries([]);
      } finally {
        setLoading(false);
      }
    };
    fetchLog();
  }, [source]);

  const sourceFilters: { key: SourceFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'validation', label: 'Validation' },
    { key: 'bot', label: 'Bot' },
    { key: 'strategist', label: 'Strategist' },
  ];

  return (
    <section className={styles.page}>
      <h1 className={styles.title}>Error Log</h1>

      <div className={styles.filterRow}>
        {sourceFilters.map((f) => (
          <button
            key={f.key}
            className={source === f.key ? styles.filterBtnActive : styles.filterBtn}
            onClick={() => setSource(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading && <SignalLoader />}

      {!loading && entries.length === 0 && (
        <div className={styles.empty}>No errors or rejections found. All clear.</div>
      )}

      {!loading && entries.length > 0 && (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Time</th>
                <th>Source</th>
                <th>Severity</th>
                <th>Symbol</th>
                <th>Action</th>
                <th>Message</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry, i) => (
                <tr key={`${entry.timestamp}-${i}`}>
                  <td className={styles.timestamp}>{formatTimestamp(entry.timestamp)}</td>
                  <td>
                    <span
                      className={`${styles.sourceBadge} ${
                        entry.source === 'validation'
                          ? styles.sourceValidation
                          : entry.source === 'bot'
                            ? styles.sourceBot
                            : styles.sourceStrategist
                      }`}
                    >
                      {entry.source}
                    </span>
                  </td>
                  <td className={entry.severity === 'error' ? styles.severityError : styles.severityWarning}>
                    {entry.severity}
                  </td>
                  <td className={styles.symbol}>{entry.symbol || '—'}</td>
                  <td>{entry.action}</td>
                  <td className={styles.message}>{entry.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default ErrorLog;
